import { Box, Button, Typography } from "@mui/material";
import type { JSX } from "react";

import { PlatformPill, ScheduleStatusPill } from "../../shared.js";
import type { ScheduledPost } from "../../types.js";
import { localTime, longDayLabel } from "../calendar-utils.js";
import { isAdjustable } from "./scheduleHelpers.js";

/**
 * The selected-day agenda — a labelled list of that day's posts (time +
 * platform + status + author), with Reschedule / Cancel for still-queued posts
 * when the viewer can edit. Extracted from `ScheduleCalendar.tsx`.
 */
export function DayAgenda({
  date,
  posts,
  canEdit,
  onReschedule,
  onCancel,
}: {
  date: Date;
  posts: ScheduledPost[];
  canEdit: boolean;
  onReschedule: (p: ScheduledPost) => void;
  onCancel: (p: ScheduledPost) => void;
}): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 1 }} data-testid="cal-agenda">
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{longDayLabel(date)}</Typography>
      {posts.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          Nothing scheduled for this day. Approve a draft on the right and it'll be
          queued at its suggested time.
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.75 }}>
          {posts.map((p) => (
            <Box
              component="li"
              key={p.id}
              data-testid={`cal-post-${p.id}`}
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "surfaceSoft",
                px: 1.5,
                py: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 12,
                  color: "text.primary",
                  minWidth: 52,
                }}
              >
                {localTime(p.scheduled_for)}
              </Box>
              <PlatformPill platform={p.platform} />
              <ScheduleStatusPill status={p.status} />
              <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                {p.scheduled_by === "human" ? "operator" : "agent"}
              </Box>
              {canEdit && isAdjustable(p) && (
                <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    data-testid={`cal-reschedule-${p.id}`}
                    onClick={() => onReschedule(p)}
                  >
                    Reschedule
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    data-testid={`cal-cancel-${p.id}`}
                    onClick={() => onCancel(p)}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
